
import React, { useState, useMemo, useEffect } from 'react';
import { StaffMember, AttendanceRecord, Shift, AttendanceRequest, ClosedPeriod, Department, Schedule, ReportPermissionLevel } from '../../types';
import { Icons } from '../ui/Icons';
import { formatDateYYYYMMDD, getWeekStartDate, calculateHours } from '../../utils/dateUtils';
import { safeFormat } from '../../utils/safeDateFns';
import { database } from '../../services/database';
import toast from 'react-hot-toast';

interface StaffWorkingHoursReportProps {
    staffList: StaffMember[];
    shifts: Shift[];
    schedule: Schedule;
    attendanceRecords: AttendanceRecord[];
    attendanceRequests: AttendanceRequest[];
    departments: Department[];
    propertyName: string;
    onInitiatePrint?: (data: any) => void;
    permissionLevel?: ReportPermissionLevel;
    initialLevel?: 'form' | 'preview' | 'history';
    onLevelChange?: (level: 'form' | 'preview' | 'history') => void;
}

const StaffWorkingHoursReport: React.FC<StaffWorkingHoursReportProps> = ({
    staffList,
    shifts,
    schedule,
    attendanceRecords,
    attendanceRequests,
    departments,
    propertyName,
    onInitiatePrint,
    permissionLevel = 'view',
    initialLevel = 'form',
    onLevelChange
}) => {
    const canGenerate = permissionLevel === 'generate' || permissionLevel === 'export';
    const canExport = permissionLevel === 'export';
    const [currentLevel, setCurrentLevel] = useState<'form' | 'preview' | 'history'>(initialLevel);

    useEffect(() => {
        setCurrentLevel(initialLevel);
    }, [initialLevel]);

    const handleLevelChange = (level: 'form' | 'preview' | 'history') => {
        setCurrentLevel(level);
        if (onLevelChange) onLevelChange(level);
    };

    const [startDate, setStartDate] = useState<string>(() => {
        const d = getWeekStartDate(new Date(), 1); // Default to this week Monday
        return formatDateYYYYMMDD(d);
    });
    const [isClosed, setIsClosed] = useState<boolean>(false);
    const [closedData, setClosedData] = useState<ClosedPeriod | null>(null);
    const [isGenerating, setIsGenerating] = useState<boolean>(false);
    const [showCloseConfirm, setShowCloseConfirm] = useState<boolean>(false);
    const [showPrintOptions, setShowPrintOptions] = useState<boolean>(false);
    const [selectedDeptIds, setSelectedDeptIds] = useState<string[]>(['all']);

    const endDate = useMemo(() => {
        const d = new Date(startDate);
        d.setDate(d.getDate() + 13);
        return formatDateYYYYMMDD(d);
    }, [startDate]);

    const periodId = useMemo(() => `${startDate}_${endDate}`, [startDate, endDate]);

    useEffect(() => {
        const checkClosed = async () => {
            const closed = await database.getClosedPeriod(periodId);
            setIsClosed(!!closed);
            setClosedData(closed);
        };
        checkClosed();
    }, [periodId]);

    const week1Dates = useMemo(() => {
        const dates = [];
        for (let i = 0; i < 7; i++) {
            const d = new Date(startDate);
            d.setDate(d.getDate() + i);
            dates.push(formatDateYYYYMMDD(d));
        }
        return dates;
    }, [startDate]);

    const week2Dates = useMemo(() => {
        const dates = [];
        for (let i = 0; i < 7; i++) {
            const d = new Date(startDate);
            d.setDate(d.getDate() + 7 + i);
            dates.push(formatDateYYYYMMDD(d));
        }
        return dates;
    }, [startDate]);

    const reportData = useMemo(() => {
        if (!isGenerating && !isClosed) return [];

        const activeStaff = staffList.filter(s => s && s.status !== 'deleted');
        
        const filteredDepts = selectedDeptIds.includes('all') 
            ? departments 
            : departments.filter(d => selectedDeptIds.includes(d.id));

        return filteredDepts.map(dept => {
            const deptStaff = activeStaff.filter(s => s.departmentIds?.includes(dept.id));
            
            const staffRows = deptStaff.map(staff => {
                const getHoursForDate = (date: string) => {
                    const entry = schedule[staff.id]?.[date];
                    if (!entry || entry.status !== 'ASSIGNED' || !entry.shiftId) return 0;

                    // Step 3: Approval/Rejection Logic
                    // EXCLUDE if explicitly rejected
                    if (entry.isRejected === true) {
                        console.log(`Skipped shift for ${staff.name} on ${date}: explicitly rejected`);
                        return 0;
                    }

                    // Step 2: Determine Effective Shift Time
                    let startTime = '';
                    let endTime = '';

                    if (entry.isTimeEdited) {
                        startTime = entry.customStartTime || '';
                        endTime = entry.customEndTime || '';
                    } else {
                        // Use scheduled times if available, otherwise fallback to shift template
                        startTime = entry.assignedStartTime || '';
                        endTime = entry.assignedEndTime || '';

                        if (!startTime || !endTime) {
                            const shiftTemplate = shifts.find(s => s.id === entry.shiftId);
                            if (shiftTemplate) {
                                startTime = shiftTemplate.startTime;
                                endTime = shiftTemplate.endTime;
                            }
                        }
                    }

                    if (!startTime || !endTime) {
                        console.error(`Skipped shift for ${staff.name} on ${date}: missing time data`, entry);
                        return 0;
                    }

                    // Step 5: Final Working Hours Calculation
                    return calculateHours(startTime, endTime);
                };

                const week1Hours = week1Dates.map(date => getHoursForDate(date));
                const week2Hours = week2Dates.map(date => getHoursForDate(date));
                
                const totalWk1 = week1Hours.reduce((sum, h) => sum + h, 0);
                const totalWk2 = week2Hours.reduce((sum, h) => sum + h, 0);
                
                return {
                    staff,
                    week1Hours,
                    week2Hours,
                    totalWk1,
                    totalWk2,
                    grandTotal: totalWk1 + totalWk2
                };
            });

            return {
                department: dept,
                staffRows
            };
        }).filter(d => d.staffRows.length > 0);
    }, [isGenerating, isClosed, staffList, departments, attendanceRecords, week1Dates, week2Dates]);

    const hasPendingApprovals = useMemo(() => {
        return attendanceRequests.some(tu => tu.status === 'PENDING' && tu.date >= startDate && tu.date <= endDate);
    }, [attendanceRequests, startDate, endDate]);

    const handleGenerate = () => {
        if (hasPendingApprovals) {
            toast.error("Cannot generate report: There are pending time update approvals in this period.");
            return;
        }
        setIsGenerating(true);
        handleLevelChange('preview');
        toast.success("Report generated successfully.");
    };

    const handleClosePeriod = async () => {
        if (!isGenerating) {
            toast.error("Please generate the report first.");
            return;
        }
        setShowCloseConfirm(true);
    };

    const confirmClosePeriod = async () => {
        const newClosedPeriod: ClosedPeriod = {
            id: periodId,
            startDate,
            endDate,
            closedAt: new Date().toISOString(),
            closedBy: 'Admin' // Should ideally be the logged in user
        };

        try {
            await database.closePeriod(newClosedPeriod);
            setIsClosed(true);
            setClosedData(newClosedPeriod);
            setShowCloseConfirm(false);
            toast.success("Period closed and locked.");
        } catch (error) {
            toast.error("Failed to close period.");
        }
    };

    const handlePrint = () => {
        setShowPrintOptions(true);
    };

    const confirmPrint = () => {
        if (onInitiatePrint) {
            onInitiatePrint({
                startDate,
                endDate,
                reportData,
                week1Dates,
                week2Dates,
                propertyName,
                selectedDeptIds
            });
        } else {
            window.print();
        }
        setShowPrintOptions(false);
    };

    const handleExportCSV = () => {
        let csvContent = "Department,Name,";
        
        // Headers for Week 1
        week1Dates.forEach(date => {
            csvContent += `${safeFormat(date, 'dd MMM')},`;
        });
        csvContent += "Total Wk1,";

        // Headers for Week 2
        week2Dates.forEach(date => {
            csvContent += `${safeFormat(date, 'dd MMM')},`;
        });
        csvContent += "Total Wk2,Grand Total\n";

        // Day names row
        csvContent += ",,";
        ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].forEach(day => csvContent += `${day},`);
        csvContent += ",";
        ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].forEach(day => csvContent += `${day},`);
        csvContent += ",\n";

        reportData.forEach(deptGroup => {
            // Department Header
            csvContent += `Department: ${deptGroup.department.name},,,,,,,,,,,,,,,,,\n`;

            deptGroup.staffRows.forEach(row => {
                const line = [
                    deptGroup.department.name,
                    row.staff.name,
                    ...row.week1Hours.map(h => h || ''),
                    row.totalWk1.toFixed(2),
                    ...row.week2Hours.map(h => h || ''),
                    row.totalWk2.toFixed(2),
                    row.grandTotal.toFixed(2)
                ].join(",");
                csvContent += line + "\n";
            });

            // Department Totals
            const deptW1Totals = week1Dates.map((_, i) => deptGroup.staffRows.reduce((sum, r) => sum + r.week1Hours[i], 0));
            const deptW2Totals = week2Dates.map((_, i) => deptGroup.staffRows.reduce((sum, r) => sum + r.week2Hours[i], 0));
            const deptTotalW1 = deptGroup.staffRows.reduce((sum, r) => sum + r.totalWk1, 0);
            const deptTotalW2 = deptGroup.staffRows.reduce((sum, r) => sum + r.totalWk2, 0);
            const deptGrandTotal = deptGroup.staffRows.reduce((sum, r) => sum + r.grandTotal, 0);

            const deptTotalLine = [
                "Department Total",
                deptGroup.department.name,
                ...deptW1Totals.map(t => t.toFixed(2)),
                deptTotalW1.toFixed(2),
                ...deptW2Totals.map(t => t.toFixed(2)),
                deptTotalW2.toFixed(2),
                deptGrandTotal.toFixed(2)
            ].join(",");
            csvContent += deptTotalLine + "\n\n";
        });

        // Grand Totals
        const grandW1Totals = week1Dates.map((_, i) => reportData.reduce((sum, dg) => sum + dg.staffRows.reduce((s, r) => s + r.week1Hours[i], 0), 0));
        const grandW2Totals = week2Dates.map((_, i) => reportData.reduce((sum, dg) => sum + dg.staffRows.reduce((s, r) => s + r.week2Hours[i], 0), 0));
        const grandTotalW1 = reportData.reduce((sum, dg) => sum + dg.staffRows.reduce((s, r) => s + r.totalWk1, 0), 0);
        const grandTotalW2 = reportData.reduce((sum, dg) => sum + dg.staffRows.reduce((s, r) => s + r.totalWk2, 0), 0);
        const grandGrandTotal = reportData.reduce((sum, dg) => sum + dg.staffRows.reduce((s, r) => s + r.grandTotal, 0), 0);

        const grandTotalLine = [
            "GRAND TOTAL",
            "",
            ...grandW1Totals.map(t => t.toFixed(2)),
            grandTotalW1.toFixed(2),
            ...grandW2Totals.map(t => t.toFixed(2)),
            grandTotalW2.toFixed(2),
            grandGrandTotal.toFixed(2)
        ].join(",");
        csvContent += grandTotalLine + "\n";

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `working_hours_${periodId}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const renderForm = () => (
        <div className="max-w-xl mx-auto">
            <div className="bg-white dark:bg-slate-900 p-8 rounded-[40px] shadow-sm border border-gray-100 dark:border-slate-800 space-y-8">
                <div className="flex items-center gap-4 mb-2">
                    <div className="w-12 h-12 bg-indigo-50 dark:bg-indigo-900/30 rounded-2xl flex items-center justify-center text-indigo-600">
                        <Icons.Calendar className="w-6 h-6" />
                    </div>
                    <div>
                        <h3 className="text-lg font-black text-gray-900 dark:text-white uppercase tracking-tight">Select Period</h3>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Biweekly Working Hours</p>
                    </div>
                </div>

                <div className="space-y-6">
                    <div>
                        <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 px-1">Departments</label>
                        <div className="grid grid-cols-2 gap-2">
                            <button
                                onClick={() => setSelectedDeptIds(['all'])}
                                className={`px-4 py-3 rounded-xl text-xs font-bold transition-all border ${
                                    selectedDeptIds.includes('all')
                                        ? 'bg-indigo-600 border-indigo-600 text-white shadow-lg shadow-indigo-100'
                                        : 'bg-gray-50 border-gray-100 text-gray-500 hover:border-indigo-200'
                                }`}
                            >
                                All Departments
                            </button>
                            {departments.map(dept => {
                                const isSelected = selectedDeptIds.includes(dept.id);
                                return (
                                    <button
                                        key={dept.id}
                                        onClick={() => {
                                            if (selectedDeptIds.includes('all')) {
                                                setSelectedDeptIds([dept.id]);
                                            } else {
                                                if (isSelected) {
                                                    const next = selectedDeptIds.filter(id => id !== dept.id);
                                                    setSelectedDeptIds(next.length === 0 ? ['all'] : next);
                                                } else {
                                                    setSelectedDeptIds([...selectedDeptIds, dept.id]);
                                                }
                                            }
                                            setIsGenerating(false);
                                        }}
                                        className={`px-4 py-3 rounded-xl text-xs font-bold transition-all border text-left flex items-center justify-between ${
                                            isSelected && !selectedDeptIds.includes('all')
                                                ? 'bg-indigo-600 border-indigo-600 text-white shadow-lg shadow-indigo-100'
                                                : 'bg-gray-50 border-gray-100 text-gray-500 hover:border-indigo-200'
                                        }`}
                                    >
                                        <span className="truncate">{dept.name}</span>
                                        {isSelected && !selectedDeptIds.includes('all') && <Icons.Check className="w-3 h-3 flex-shrink-0" />}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    
                    <div>
                        <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 px-1">Period Start (Monday)</label>
                        <input 
                            type="date" 
                            value={startDate}
                            onChange={(e) => {
                                setStartDate(e.target.value);
                                setIsGenerating(false);
                            }}
                            disabled={isClosed}
                            className="w-full bg-gray-50 dark:bg-slate-800 border-none rounded-2xl p-4 font-bold text-sm focus:ring-2 focus:ring-indigo-500 transition-all disabled:opacity-50"
                        />
                    </div>
                    
                    <div className="p-4 bg-indigo-50/30 dark:bg-indigo-900/10 rounded-2xl border border-indigo-100/50 dark:border-indigo-900/30 flex items-center justify-between">
                        <span className="text-xs font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-widest">Selected Range:</span>
                        <span className="text-sm font-black text-indigo-700 dark:text-indigo-300">
                            {safeFormat(startDate, 'MMM d')} — {safeFormat(endDate, 'MMM d, yyyy')}
                        </span>
                    </div>

                    {isClosed && (
                         <div className="flex items-center gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 rounded-2xl border border-amber-200 dark:border-amber-800 text-xs font-bold">
                            <Icons.Lock className="w-4 h-4" />
                            Period Closed on {safeFormat(closedData?.closedAt, 'MMM d, yyyy h:mm a')}
                        </div>
                    )}

                    <div className="pt-4">
                        {canGenerate && (
                            <button 
                                onClick={handleGenerate}
                                className="w-full bg-indigo-600 text-white py-6 rounded-[32px] font-black uppercase tracking-widest shadow-2xl shadow-indigo-200 dark:shadow-none hover:bg-indigo-700 transition-all flex items-center justify-center gap-3 group"
                            >
                                Generate Report
                                <Icons.ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                            </button>
                        )}
                        
                        {isClosed && (
                            <button 
                                onClick={() => handleLevelChange('preview')}
                                className="w-full mt-4 py-4 text-indigo-600 font-bold text-xs uppercase tracking-widest hover:underline"
                            >
                                View Previously Generated Report
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    const renderPreview = () => (
        <div className="space-y-6">
            <div className="bg-white dark:bg-slate-900 p-6 rounded-[32px] border border-gray-100 dark:border-slate-800 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4 no-print">
                <div className="flex items-center gap-4">
                    <button 
                        onClick={() => handleLevelChange('form')}
                        className="w-10 h-10 rounded-full bg-gray-50 dark:bg-slate-800 flex items-center justify-center hover:bg-gray-100 transition-colors"
                    >
                        <Icons.ArrowLeft className="w-5 h-5 text-gray-600" />
                    </button>
                    <div>
                        <h2 className="text-lg font-black text-gray-900 dark:text-white uppercase tracking-tight">Report Preview</h2>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{safeFormat(startDate, 'MMM d')} — {safeFormat(endDate, 'MMM d')}</p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    {canExport && (
                        <>
                            <button 
                                onClick={handlePrint}
                                className="px-4 py-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-xs font-bold hover:bg-gray-50 transition-all flex items-center gap-2"
                            >
                                <Icons.Printer className="w-4 h-4" />
                                Print
                            </button>
                            <button 
                                onClick={handleExportCSV}
                                className="px-4 py-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-xs font-bold hover:bg-gray-50 transition-all flex items-center gap-2"
                            >
                                <Icons.Download className="w-4 h-4" />
                                CSV
                            </button>
                        </>
                    )}
                    {!isClosed && canGenerate && (
                        <button 
                            onClick={handleClosePeriod}
                            className="px-6 py-2 bg-rose-600 text-white font-bold rounded-xl hover:bg-rose-700 transition-all shadow-md flex items-center gap-2"
                        >
                            <Icons.Lock className="w-4 h-4" />
                            Close & Lock
                        </button>
                    )}
                </div>
            </div>

            <div className="bg-white p-8 rounded-[40px] shadow-sm border border-gray-200 overflow-x-auto print:shadow-none print:border-0 print:p-0">
                <div className="min-w-[1200px]">
                    <div className="text-center mb-10 pb-6 border-b border-gray-100">
                        <h1 className="text-2xl font-black uppercase tracking-tight mb-2">Staff Working Hours Report</h1>
                        <p className="text-xs font-bold text-indigo-600 uppercase tracking-widest">
                            Property: {propertyName} • Date Range: {safeFormat(startDate, 'dd MMM')} TO {safeFormat(endDate, 'dd MMM, yyyy')}
                        </p>
                    </div>

                    <table className="w-full border-collapse text-[11px]">
                        <thead>
                            <tr className="bg-gray-900 text-white">
                                <th className="border border-gray-700 px-4 py-3 text-left w-64 font-black uppercase tracking-wider">STAFF MEMBER</th>
                                {week1Dates.map(date => (
                                    <th key={date} className="border border-gray-700 px-1 py-3 text-center w-20 font-black uppercase tracking-wider">
                                        {safeFormat(date, 'dd MMM')}<br/>
                                        <span className="text-[8px] opacity-70">{safeFormat(date, 'EEEE').substring(0, 3)}</span>
                                    </th>
                                ))}
                                <th className="border border-gray-700 px-3 py-3 text-center bg-gray-800 font-black w-24 uppercase tracking-wider">WK1 TOTAL</th>
                                {week2Dates.map(date => (
                                    <th key={date} className="border border-gray-700 px-1 py-3 text-center w-20 font-black uppercase tracking-wider">
                                        {safeFormat(date, 'dd MMM')}<br/>
                                        <span className="text-[8px] opacity-70">{safeFormat(date, 'EEEE').substring(0, 3)}</span>
                                    </th>
                                ))}
                                <th className="border border-gray-700 px-3 py-3 text-center bg-gray-800 font-black w-24 uppercase tracking-wider">WK2 TOTAL</th>
                                <th className="border border-gray-700 px-3 py-3 text-center bg-indigo-900 font-black w-28 uppercase tracking-wider">GRAND TOTAL</th>
                            </tr>
                        </thead>
                        <tbody>
                            {reportData.map(deptGroup => (
                                <React.Fragment key={deptGroup.department.id}>
                                    <tr className="bg-gray-100/80">
                                        <td colSpan={18} className="border border-gray-300 px-4 py-2 font-black uppercase text-[12px] tracking-tight text-gray-700">
                                            Department : {deptGroup.department.name}
                                        </td>
                                    </tr>
                                    {deptGroup.staffRows.map(row => (
                                        <tr key={row.staff.id} className="hover:bg-indigo-50/30 transition-colors">
                                            <td className="border border-gray-200 px-4 py-3 font-bold uppercase text-gray-900">{row.staff.name}</td>
                                            {row.week1Hours.map((h, i) => (
                                                <td key={`h1-${i}`} className={`border border-gray-200 px-1 py-3 text-center font-mono ${h > 0 ? 'text-indigo-600 font-black' : 'text-gray-200'}`}>
                                                    {h > 0 ? h.toFixed(2) : '—'}
                                                </td>
                                            ))}
                                            <td className="border border-gray-300 px-2 py-3 text-center font-mono font-black bg-gray-50/50 text-gray-900">{row.totalWk1.toFixed(2)}</td>
                                            {row.week2Hours.map((h, i) => (
                                                <td key={`h2-${i}`} className={`border border-gray-200 px-1 py-3 text-center font-mono ${h > 0 ? 'text-indigo-600 font-black' : 'text-gray-200'}`}>
                                                    {h > 0 ? h.toFixed(2) : '—'}
                                                </td>
                                            ))}
                                            <td className="border border-gray-300 px-2 py-3 text-center font-mono font-black bg-gray-50/50 text-gray-900">{row.totalWk2.toFixed(2)}</td>
                                            <td className="border border-gray-300 px-2 py-3 text-center font-mono font-black bg-indigo-50/30 text-indigo-700 text-sm">{row.grandTotal.toFixed(2)}</td>
                                        </tr>
                                    ))}
                                    <tr className="bg-gray-50 font-black border-t-2 border-gray-300">
                                        <td className="border border-gray-300 px-4 py-2 text-right uppercase text-[10px]">Dept {deptGroup.department.name} Totals:</td>
                                        {week1Dates.map((_, i) => (
                                            <td key={`dt1-${i}`} className="border border-gray-300 px-1 py-2 text-center font-mono text-gray-600">
                                                {deptGroup.staffRows.reduce((sum, r) => sum + r.week1Hours[i], 0).toFixed(2)}
                                            </td>
                                        ))}
                                        <td className="border border-gray-300 px-2 py-2 text-center font-mono text-gray-900 bg-gray-100">
                                            {deptGroup.staffRows.reduce((sum, r) => sum + r.totalWk1, 0).toFixed(2)}
                                        </td>
                                        {week2Dates.map((_, i) => (
                                            <td key={`dt2-${i}`} className="border border-gray-300 px-1 py-2 text-center font-mono text-gray-600">
                                                {deptGroup.staffRows.reduce((sum, r) => sum + r.week2Hours[i], 0).toFixed(2)}
                                            </td>
                                        ))}
                                        <td className="border border-gray-300 px-2 py-2 text-center font-mono text-gray-900 bg-gray-100">
                                            {deptGroup.staffRows.reduce((sum, r) => sum + r.totalWk2, 0).toFixed(2)}
                                        </td>
                                        <td className="border border-gray-300 px-2 py-2 text-center font-mono text-indigo-900 bg-indigo-100/50">
                                            {deptGroup.staffRows.reduce((sum, r) => sum + r.grandTotal, 0).toFixed(2)}
                                        </td>
                                    </tr>
                                </React.Fragment>
                            ))}
                        </tbody>
                        <tfoot className="border-t-4 border-gray-900">
                            <tr className="bg-gray-900 text-white font-black">
                                <td className="border border-gray-800 px-4 py-4 text-right uppercase tracking-widest text-[12px]">GRAND TOTAL OF HOURS :</td>
                                {week1Dates.map((_, i) => (
                                    <td key={`gt1-${i}`} className="border border-gray-800 px-1 py-4 text-center font-mono text-gray-400">
                                        {reportData.reduce((sum, dg) => sum + dg.staffRows.reduce((s, r) => s + r.week1Hours[i], 0), 0).toFixed(2)}
                                    </td>
                                ))}
                                <td className="border border-gray-800 px-2 py-4 text-center font-mono bg-gray-800">
                                    {reportData.reduce((sum, dg) => sum + dg.staffRows.reduce((s, r) => s + r.totalWk1, 0), 0).toFixed(2)}
                                </td>
                                {week2Dates.map((_, i) => (
                                    <td key={`gt2-${i}`} className="border border-gray-800 px-1 py-4 text-center font-mono text-gray-400">
                                        {reportData.reduce((sum, dg) => sum + dg.staffRows.reduce((s, r) => s + r.week2Hours[i], 0), 0).toFixed(2)}
                                    </td>
                                ))}
                                <td className="border border-gray-800 px-2 py-4 text-center font-mono bg-gray-800">
                                    {reportData.reduce((sum, dg) => sum + dg.staffRows.reduce((s, r) => s + r.totalWk2, 0), 0).toFixed(2)}
                                </td>
                                <td className="border border-gray-800 px-2 py-4 text-center font-mono bg-indigo-600 text-lg">
                                    {reportData.reduce((sum, dg) => sum + dg.staffRows.reduce((s, r) => s + r.grandTotal, 0), 0).toFixed(2)}
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        </div>
    );

    return (
        <div className="space-y-6">
            {/* Print Options Modal */}
            {showPrintOptions && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 no-print">
                    <div className="bg-white dark:bg-slate-800 rounded-3xl p-8 max-w-md w-full shadow-2xl border border-gray-200 dark:border-slate-700">
                        <div className="flex items-center gap-3 text-indigo-600 mb-6">
                            <Icons.Printer className="w-8 h-8" />
                            <h3 className="text-xl font-black uppercase tracking-tight">Print Options</h3>
                        </div>
                        <div className="space-y-6 mb-8">
                            <div className="p-4 bg-indigo-50 dark:bg-indigo-900/30 rounded-2xl border border-indigo-100 dark:border-indigo-800">
                                <h4 className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-2">Selected Departments</h4>
                                <div className="flex flex-wrap gap-2">
                                    {selectedDeptIds.includes('all') ? (
                                        <span className="px-3 py-1 bg-indigo-600 text-white text-[10px] font-bold rounded-lg uppercase tracking-wider">All Departments</span>
                                    ) : (
                                        selectedDeptIds.map(id => {
                                            const dept = departments.find(d => d.id === id);
                                            return dept ? (
                                                <span key={id} className="px-3 py-1 bg-indigo-600 text-white text-[10px] font-bold rounded-lg uppercase tracking-wider">{dept.name}</span>
                                            ) : null;
                                        })
                                    )}
                                </div>
                            </div>
                            <p className="text-[10px] text-gray-500 dark:text-gray-400 font-bold uppercase tracking-widest leading-relaxed">
                                * The report will print each selected department on its own landscape page.
                            </p>
                        </div>
                        <div className="flex items-center justify-end gap-4">
                            <button 
                                onClick={() => setShowPrintOptions(false)}
                                className="px-6 py-3 text-gray-500 font-bold hover:text-gray-700 transition-colors uppercase tracking-widest text-xs"
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={confirmPrint}
                                className="px-8 py-3 bg-indigo-600 text-white font-black rounded-xl hover:bg-indigo-700 transition-all shadow-lg flex items-center gap-2 uppercase tracking-widest text-xs"
                            >
                                <Icons.Printer className="w-4 h-4" />
                                Start Printing
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirmation Modal */}
            {showCloseConfirm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 no-print">
                    <div className="bg-white dark:bg-slate-800 rounded-3xl p-8 max-w-md w-full shadow-2xl border border-gray-200 dark:border-slate-700">
                        <div className="flex items-center gap-3 text-rose-600 mb-6">
                            <div className="w-12 h-12 bg-rose-50 dark:bg-rose-900/30 rounded-2xl flex items-center justify-center">
                                <Icons.AlertTriangle className="w-6 h-6" />
                            </div>
                            <h3 className="text-xl font-black uppercase tracking-tight">Close Period?</h3>
                        </div>
                        <p className="text-gray-600 dark:text-gray-400 mb-8 font-medium leading-relaxed">
                            Are you sure you want to lock this biweekly period? This action is <span className="text-rose-600 font-bold italic">irreversible</span>. The data will be archived and no further changes to schedule or times will be reflected in this specific locked report.
                        </p>
                        <div className="flex items-center justify-end gap-4">
                            <button 
                                onClick={() => setShowCloseConfirm(false)}
                                className="px-6 py-3 text-gray-500 font-bold hover:text-gray-700 transition-colors uppercase tracking-widest text-xs"
                            >
                                Back
                            </button>
                            <button 
                                onClick={confirmClosePeriod}
                                className="px-8 py-3 bg-rose-600 text-white font-black rounded-xl hover:bg-rose-700 transition-all shadow-lg uppercase tracking-widest text-xs"
                            >
                                Yes, Lock Period
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {currentLevel === 'form' ? renderForm() : renderPreview()}

            <style dangerouslySetInnerHTML={{ __html: `
                @media print {
                    .no-print {
                        display: none !important;
                    }
                    body {
                        background: white !important;
                        padding: 0 !important;
                        margin: 0 !important;
                    }
                    @page {
                        size: landscape;
                        margin: 8mm;
                    }
                    table { page-break-inside: auto; }
                    tr { page-break-inside: avoid; page-break-after: auto; }
                    thead { display: table-header-group; }
                    tfoot { display: table-footer-group; }
                }
            `}} />
        </div>
    );
};

export default StaffWorkingHoursReport;
